<script setup lang="ts">
import { ref } from 'vue'

import type { CapabilityProfile, UserRow } from '../types/callables'
import BaseDialog from '../ui/components/BaseDialog.vue'
import { useDialog } from '../ui/dialogs'

export interface AssignProfilesPayload {
  uid:            string
  profiles:       string[]
  defaultProfile: string | null
}

const props = defineProps<{
  user:     UserRow
  profiles: CapabilityProfile[]
  save:     (payload: AssignProfilesPayload) => Promise<boolean>
}>()

const { close } = useDialog<AssignProfilesPayload>()

// Filter to profiles that still exist — an assigned id whose profile has
// been deleted (only possible if the delete used `force`) should not survive
// a re-save silently.
const known = new Set(props.profiles.map((p) => p.id))
const selected = ref<Set<string>>(new Set(props.user.profiles.filter((id) => known.has(id))))
const defaultProfile = ref<string | null>(
  props.user.defaultProfile && known.has(props.user.defaultProfile) ? props.user.defaultProfile : null,
)

const busy       = ref(false)
const localError = ref<string | null>(null)

function toggle(id: string, on: boolean) {
  const next = new Set(selected.value)
  if (on) next.add(id)
  else {
    next.delete(id)
    // Clearing an assignment also clears the default if it pointed at that
    // profile — otherwise the server rejects with default-not-in-profiles
    // and the admin has to re-open the dialog to reconcile.
    if (defaultProfile.value === id) defaultProfile.value = null
  }
  selected.value = next
}

async function onSubmit() {
  localError.value = null

  const list = [...selected.value]
  if (defaultProfile.value && !list.includes(defaultProfile.value)) {
    localError.value = 'Default profile must be one of the assigned profiles.'
    return
  }
  const payload: AssignProfilesPayload = {
    uid: props.user.uid,
    profiles: list,
    defaultProfile: defaultProfile.value,
  }
  busy.value = true
  try {
    if (await props.save(payload)) close(payload)
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <BaseDialog title="Assign profiles" @dismiss="close()">
    <p class="hint">
      <strong>{{ user.email ?? user.uid }}</strong> receives a licence with
      the selected profile's capability set. Assign more than one to let the
      user pick between them when opening the app.
    </p>

    <form id="assign-profiles-form" @submit.prevent="onSubmit">
      <fieldset :disabled="busy" class="assign">
        <legend>Assigned profiles</legend>
        <p v-if="!profiles.length" class="empty">
          No profiles exist yet. Create one first.
        </p>
        <label v-for="p in profiles" :key="p.id" class="row">
          <input
            type="checkbox"
            class="checkbox"
            :checked="selected.has(p.id)"
            @change="toggle(p.id, ($event.target as HTMLInputElement).checked)"
          />
          <span class="pname">{{ p.name }}</span>
          <label class="default-cell">
            <input
              type="radio"
              class="radio"
              name="default-profile"
              :value="p.id"
              :checked="defaultProfile === p.id"
              :disabled="!selected.has(p.id)"
              @change="defaultProfile = p.id"
            />
            <span class="default-label">default</span>
          </label>
        </label>
        <button
          v-if="defaultProfile"
          type="button"
          class="btn-link clear-default"
          :disabled="busy"
          @click="defaultProfile = null"
        >
          Clear default
        </button>
      </fieldset>

      <p v-if="localError" class="field-error" role="alert">{{ localError }}</p>
    </form>

    <template #actions>
      <button type="button" class="btn btn-secondary" :disabled="busy" @click="close()">
        Cancel
      </button>
      <button type="submit" form="assign-profiles-form" class="btn btn-primary" :disabled="busy || !profiles.length">
        {{ busy ? 'Saving…' : 'Save assignments' }}
      </button>
    </template>
  </BaseDialog>
</template>

<style scoped>
/* Layout unique to this dialog: three-column row (checkbox, name, default
   radio) inside a titled fieldset. */
.assign {
  display: flex;
  flex-direction: column;
  gap: var(--sp-1);
}

.row {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: var(--sp-2);
  font-size: 0.88rem;
  cursor: pointer;
}

.pname { font-weight: 500; }

.default-cell {
  display: flex;
  align-items: center;
  gap: var(--sp-1);
  font-size: 0.78rem;
  color: var(--fg-muted);
  cursor: pointer;
}

.default-cell input:disabled + .default-label {
  opacity: 0.4;
  cursor: default;
}

.clear-default {
  align-self: flex-end;
  font-size: 0.78rem;
  padding: var(--sp-1) var(--sp-2);
}

.empty {
  margin: var(--sp-1) 0;
  font-size: 0.85rem;
  color: var(--fg-muted);
}
</style>
